import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Header } from './header';
import { SectionStatusType } from 'types';
import { CustomRequest, IDocument } from '../../../../../types';
import { CustomRequestDocumentsSponsorTypesEnum } from '../../../../../types/enums';
import NoDocuments from '../../../../atoms/no-doc-placeholder';
import { StampedVisaVignetteDocs } from '../../../../molecules/followUpDocumentSection/afterArrival/stampedVisaVignette';
import { NewStudentVisaVignetteModal } from '../../../modals/followUpDocs/newStudentVisaVignetteModal';
import { BiometricResidencePermitDocs } from '../../../../molecules/followUpDocumentSection/afterArrival/biometricResidencePermit';
import { BiometricResidencePermitModal } from '../../../modals/followUpDocs/biometricResidencePermitModal';
import { VisaVignetteDoc } from 'types/dist/followUpDocuments/shared';
import CustomRequestModal from '../../../modals/followUpDocs/customRequestModal';
import CustomRequestDocument from '../../../../molecules/followUpDocumentSection/customRequest';
import SectionHeader from '../../../../atoms/SectionHeader';
import { titleAppend } from '../../../../../utilities/head';
import { useDispatch, useSelector } from 'react-redux';
import * as uiActions from '../../../../../store/ui/actions';
import { createNavigateToAction } from '../../../../../middleware/actions';
import * as studentActions from '../../../../../store/student/actions';
import { SectionId } from '../../../../molecules/Sidebar/types';
import { ApplicationState } from 'frontend/student/src/store';
import { hasAnyMatchingFollowUpDocs } from '../../../../../utilities/documents';
import { BiometricResidencePermitDoc } from '../../../../../types';
import FileUploadLocal from '../../../modals/fileUpload/FileUploadLocal';

const AfterArrivalDocuments = () => {
  const student = useSelector((state: ApplicationState) => state.student);
  const hasRequiredAfterArrivalDocs = hasAnyMatchingFollowUpDocs(student.followUpDocuments, 'afterArrival', 'required');

  const afterArrivalDocAreaStatus = useSelector((state: ApplicationState) => state.config.afterArrivalDocAreaStatus);
  const followUpDocuments = useSelector((state: ApplicationState) => state.student.followUpDocuments);
  const summary = useSelector((state: ApplicationState) => state.student.summary);
  const config = useSelector((state: ApplicationState) => state.config.sections.FollowUpDocuments);
  const travelStatus = useSelector((state: ApplicationState) => state.student.enrolmentStatus?.travelStatusSection);
  const openModal = useSelector((state: ApplicationState) => state.ui.openModal);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(studentActions.getStudentFollowUpDocs());
  }, [dispatch]);

  const goToTravelStatusTab = () => {
    dispatch(uiActions.updateActiveSectionId(SectionId.ENROLMENT));
    dispatch(createNavigateToAction('/dashboard?isModalOpen=true'));
  };
  const saveFollowUpDocumentsBRPWillNotReceiving = (docKey: string, reason: string) => {
    dispatch(studentActions.saveFollowUpDocumentsBRPWillNotReceiving(docKey, reason));
    dispatch(uiActions.closeModal());
  };

  const openUploadModal = (
    docKey: string,
    docType: string,
    sponsorType: string,
    sponsorKey: string | null,
    modalTitle: string,
    fileNames: string[],
    metadata?: any
  ) => {
    dispatch(
      uiActions.openUploadFollowUpModal(sponsorType, sponsorKey, docType, docKey, modalTitle, fileNames, metadata)
    );
  };
  const openDocumentModal = (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string,
    docKey: string,
    modalTitle: string,
    fileNames: string[],
    isComplete: boolean,
    biometricResidencePermit?: BiometricResidencePermitDoc
  ) => {
    dispatch(
      uiActions.openFollowUpDocumentModal(
        sponsorType,
        sponsorKey,
        docType,
        docKey,
        modalTitle,
        fileNames,
        isComplete,
        biometricResidencePermit
      )
    );
  };
  const closeModal = () => {
    dispatch(uiActions.closeModal());
  };

  const getModal = () => {
    if (openModal?.isUpload) {
      return <FileUploadLocal openModal={openModal} />;
    }
    switch (openModal?.docType) {
      // AFTER ARRIVAL
      case 'stampedVisaVignette': {
        const doc: VisaVignetteDoc = followUpDocuments.afterArrival.stampedVisaVignette[openModal.docKey];
        const { fileNames, visaStartDate, visaEndDate } = doc;
        return (
          <NewStudentVisaVignetteModal
            docType="stampedVisaVignette"
            sponsorType="afterArrival"
            docKey={openModal.docKey}
            isComplete={openModal.isComplete}
            fileNames={fileNames ?? []}
            closeModal={closeModal}
            openUploadModal={openUploadModal}
            visaStartDate={visaStartDate}
            visaEndDate={visaEndDate}
          />
        );
      }
      case 'biometricResidencePermit': {
        return (
          <BiometricResidencePermitModal
            docType="biometricResidencePermit"
            sponsorType="afterArrival"
            docKey={openModal.docKey}
            isComplete={openModal.isComplete}
            biometricResidencePermit={openModal.biometricResidencePermit}
            closeModal={closeModal}
            openUploadModal={openUploadModal}
            saveFollowUpDocumentsBRPWillNotReceiving={saveFollowUpDocumentsBRPWillNotReceiving}
          />
        );
      }
      // END AFTER ARRIVAL

      case 'customRequest': {
        const { sponsorType, sponsorKey, docKey } = openModal;
        const customReqDoc: CustomRequest =
          followUpDocuments[sponsorType as 'beforeTravel' | 'afterArrival'].customRequest[sponsorKey];
        const doc = customReqDoc[docKey];

        return (
          <CustomRequestModal
            sponsorKey={openModal.sponsorKey}
            complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
            fileNames={doc.fileNames}
            sectionTitle={'Custom Document'}
            label={doc.label ?? ''}
            description={doc.desc ?? ''}
            onContinue={closeModal}
            document={doc}
            openUploadModal={() =>
              openUploadModal(
                docKey,
                'customRequest',
                sponsorType,
                sponsorKey,
                'Custom request document',
                doc.fileNames
              )
            }
            onClose={closeModal}
          />
        );
      }
      default:
        return null;
    }
  };
  const pageTitle = 'After Arrival in the UK';

  const renderAfterArrivalDocs = () => {
    return (
      <>
        <SectionHeader
          title={pageTitle}
          subtext="Please upload the following documents after arrival."
          data-selector="after-arrival-area"
        />

        <StampedVisaVignetteDocs
          openDocumentModal={openDocumentModal}
          documents={followUpDocuments.afterArrival.stampedVisaVignette}
          travelStatus={travelStatus}
        />

        <BiometricResidencePermitDocs
          openDocumentModal={openDocumentModal}
          documents={followUpDocuments.afterArrival.biometricResidencePermit}
          travelStatus={travelStatus}
        />

        <CustomRequestDocument
          sponsorType={CustomRequestDocumentsSponsorTypesEnum.AFTER_ARRIVAL}
          openDocumentModal={openDocumentModal}
          documents={followUpDocuments.afterArrival.customRequest}
        />
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>
          {pageTitle} {titleAppend}
        </title>
      </Helmet>

      {getModal()}

      <Header config={config} goToTravelStatusTab={goToTravelStatusTab} />

      {summary.followUpDocsTotal > 0 && afterArrivalDocAreaStatus === SectionStatusType.ACTIVE ? (
        <>{hasRequiredAfterArrivalDocs ? renderAfterArrivalDocs() : null}</>
      ) : (
        <NoDocuments />
      )}
    </>
  );
};

export default AfterArrivalDocuments;
